/**
 * A reply, streamed a few characters at a time, cut into what the voice says
 * next. Each piece goes to `/speak` on its own, so the first one wants to be
 * short — the wait before the voice starts is the wait for it — and the rest
 * want to be whole sentences, which a voice reads with the right fall.
 *
 * Code fences are dropped as they stream, before a sentence can form around
 * one. Everything handed out has been through `toSpeech`.
 */

import { toSpeech } from './speechText';

/** Words that end in a full stop without ending the sentence. */
const ABBREVIATIONS = /\b(mr|mrs|ms|dr|st|vs|etc|e\.g|i\.e|no|approx|fig|mt|jr|sr|inc|ltd)\.$/i;
/** What may close around a sentence's last stop. */
const CLOSERS = `*_)"'”’]`;
/** The first piece may end at a comma once it is this long. */
const EARLY = 40;
/** A sentence longer than this is cut at its last comma or space. */
const LONGEST = 240;

export class SentenceSplitter {
  private buf = '';
  private inFence = false;
  private said = 0;

  /** Text in; pieces ready to say out. */
  push(delta: string): string[] {
    this.buf += delta;
    this.dropCode();
    const out: string[] = [];
    for (;;) {
      const end = this.boundary();
      if (end < 0) break;
      this.emit(this.buf.slice(0, end), out);
      this.buf = this.buf.slice(end);
    }
    return out;
  }

  /** The reply is over: whatever is left. */
  flush(): string[] {
    const out: string[] = [];
    if (!this.inFence) this.emit(this.buf, out);
    this.buf = '';
    this.inFence = false;
    return out;
  }

  private emit(raw: string, out: string[]) {
    const text = toSpeech(raw);
    if (/[\p{L}\p{N}]/u.test(text)) {
      out.push(text);
      this.said++;
    }
  }

  /** Takes code fences, and everything inside them, out of the buffer. A
   *  fence still arriving (a lone backtick or two at the end) is held. */
  private dropCode() {
    for (;;) {
      if (this.inFence) {
        const close = this.buf.indexOf('```');
        if (close < 0) {
          // Keep only what could be the start of the closing fence.
          this.buf = this.buf.slice(Math.max(0, this.buf.length - 2));
          return;
        }
        const lineEnd = this.buf.indexOf('\n', close + 3);
        this.buf = lineEnd < 0 ? '' : this.buf.slice(lineEnd + 1);
        this.inFence = false;
        continue;
      }
      const open = this.buf.indexOf('```');
      if (open < 0) return;
      // The prose before the fence is a piece of its own.
      this.pending.push(this.buf.slice(0, open));
      this.buf = this.buf.slice(open + 3);
      this.inFence = true;
    }
  }

  private pending: string[] = [];

  /** Where the next piece ends in the buffer, or -1 while there is none. */
  private boundary(): number {
    if (this.pending.length) {
      // Prose that stood before a code fence: say it as it is.
      const before = this.pending.join(' ');
      this.pending = [];
      this.buf = before + '\n\n' + this.buf;
      return before.length + 2;
    }
    const text = this.buf;
    for (let i = 0; i < text.length; i++) {
      const c = text[i]!;
      const next = text[i + 1];
      if (c === '\n' && next === '\n') return i + 2;
      if (c === '.' || c === '!' || c === '?') {
        // Closing marks may sit between the stop and the space: `**Yes.** No`.
        let j = i + 1;
        while (j < text.length && CLOSERS.includes(text[j]!)) j++;
        if (j >= text.length || !/\s/.test(text[j]!)) continue;
        if (c === '.' && ABBREVIATIONS.test(text.slice(Math.max(0, i - 6), i + 1))) continue;
        return j;
      }
    }
    if (this.said === 0 && text.length >= EARLY) {
      const comma = text.search(/[,;:]\s(?=\S)/);
      if (comma >= 20) return comma + 1;
    }
    if (text.length > LONGEST) {
      const cut = Math.max(text.lastIndexOf(', ', LONGEST), text.lastIndexOf(' ', LONGEST));
      return cut > 0 ? cut + 1 : LONGEST;
    }
    return -1;
  }
}
